import { format, isValid, parse } from 'date-fns';
import React from 'react';

import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { useInlineEdit } from '../hooks/useInlineEdit';
import { InfoBadge } from './InfoBadge';
import { InlineEditControls } from './InlineEditControls';

interface DateOfLossEditBadgeProps {
  matterId: string;
  value: string | undefined | null;
  className?: string;
}

const formatDateForDisplay = (
  dateString: string | undefined | null,
): string => {
  if (!dateString) return 'Not set';

  try {
    // Try parsing as ISO date first (YYYY-MM-DD)
    const parsed = parse(dateString, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) {
      return format(parsed, 'MM/dd/yyyy');
    }

    // If that fails, try direct Date parsing
    const date = new Date(dateString);
    if (isValid(date)) {
      return format(date, 'MM/dd/yyyy');
    }
  } catch {
    // Silent catch - return original string on error
  }

  return dateString;
};

export const DateOfLossEditBadge: React.FC<DateOfLossEditBadgeProps> = ({
  matterId,
  value,
  className,
}) => {
  const {
    isEditing,
    setIsEditing,
    editValue,
    setEditValue,
    handleSave,
    handleCancel,
    isLoading,
  } = useInlineEdit({
    matterId,
    initialValue: value || '',
    fieldName: 'date_of_loss',
    onSuccessMessage: 'Date of loss updated',
    transformValue: (val) => val || undefined,
  });

  const displayValue = formatDateForDisplay(value);
  const isNotSet = !value;

  return (
    <Popover
      open={isEditing}
      onOpenChange={(open) => {
        if (!open) handleCancel();
        else setIsEditing(true);
      }}
    >
      <PopoverTrigger asChild>
        <InfoBadge
          label="DOL"
          value={displayValue}
          onClick={() => setIsEditing(true)}
          showLabel
          className={`border ${isNotSet ? 'bg-zinc-50 text-zinc-500 border-zinc-200' : 'bg-transparent hover:bg-zinc-50 border-transparent hover:border-zinc-100'} hover:opacity-80 ${className || ''}`}
        />
      </PopoverTrigger>
      <PopoverContent className="p-2 w-auto" align="start" side="bottom">
        <InlineEditControls
          isEditing={isEditing}
          onSave={handleSave}
          onCancel={handleCancel}
          isLoading={isLoading}
        >
          <div className="flex items-center gap-1">
            <span className="text-xs text-zinc-500 whitespace-nowrap">
              DOL:
            </span>
            <Input
              type="date"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="h-6 text-xs w-32 px-1 py-0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
          </div>
        </InlineEditControls>
      </PopoverContent>
    </Popover>
  );
};
